import { Component, OnInit, inject } from "@angular/core";
import { OAuthService } from "angular-oauth2-oidc";
import { TokenService } from "@auth/services/token/token.service";
import { NavigationEnd, Params, Router, RouterOutlet } from "@angular/router";
import { SessionStorageService } from "@shared/services/session-storage.service";
import { SessionStorageKeys } from "@shared/services/static/session-storage-keys";
import { TabsStateService } from "@shared/services/tabs-state/tabs-state.service";
import { AppConfigService } from "@api/services/app-config.service";
import { AppInsightsService } from "@api/services/app-insights/app-insights.service";
import { ICenter } from "@shared/models/center";
import { ITab } from "@shared/models/tab";
import { CentersService } from "@shared/services/api/centers/centers.service";
import { AuthService } from "@auth/services/auth/auth.service";

@Component({
    selector: "app-root",
    imports: [RouterOutlet],
    template: `
        <router-outlet></router-outlet>
    `,
})
export class AppComponent implements OnInit {
    private readonly router = inject(Router);
    private readonly oauthService = inject(OAuthService);
    private readonly tokenService = inject(TokenService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly tabsService = inject(TabsStateService);
    private readonly authService = inject(AuthService);
    private readonly appConfigService = inject(AppConfigService);
    private readonly appInsightsService = inject(AppInsightsService);
    private readonly centersService = inject(CentersService);

    private isInitialized = false;

    ngOnInit() {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd && this.appConfigService.configLoaded()) {
                this.appInsightsService.logPageView(event.urlAfterRedirects);
                this.sessionStorageService.setItem(SessionStorageKeys.LastUrl, event.url);
            }
        });

        // NOTE - BS - this accounts for browser refreshes
        if (this.tokenService.isTokenClaimsValid()) {
            this.initializeApp();
        }

        // NOTE - BS - this accounts for normal login flow. Wait for the signing callback to
        //             notify when auth has completed before proceeding.
        this.authService.authenticationCompleted$.subscribe(() => {
            this.initializeApp();
        });
    }

    private initializeApp(): void {
        if (this.isInitialized) return;

        this.isInitialized = true;
        this.sessionStorageService.clearAcwState();

        this.centersService.centers$.subscribe((availableCenters) => {
            if (this.oauthService.getAccessToken() != null && !this.tokenService.isTokenClaimsValid()) {
                this.sessionStorageService.clearAllState();
                this.oauthService.initCodeFlow();
                return;
            }

            if (this.tokenService.isTokenClaimsValid()) {
                const activeTab = this.tabsService.getActiveTab();
                const lastKnownUrl = this.sessionStorageService.getItem(SessionStorageKeys.LastUrl) ?? "/";
                const hasStoredCenterId = this.sessionStorageService.getItem(SessionStorageKeys.ScopedCenter) !== null;

                if (availableCenters.length === 1) {
                    const center = availableCenters[0];
                    this.centersService.setScopedCenter(center);
                }

                const [url, queryParams] = this.createRoutingArgs(
                    hasStoredCenterId,
                    availableCenters,
                    activeTab,
                    lastKnownUrl,
                );

                this.router.navigate([url], { queryParams });
            }
        });
    }

    private createRoutingArgs(
        hasStoredCenterId: boolean,
        availableCenters: ICenter[],
        activeTab: ITab | null,
        lastKnownUrl: string,
    ): [string, Params] {
        let url = lastKnownUrl;
        let queryParams = {};

        if (!availableCenters.length) {
            url = "unauthorized";
        } else if (availableCenters.length > 1 && !hasStoredCenterId) {
            url = "centers";
        } else if (activeTab) {
            url = activeTab.tabComponentUrl;
            queryParams = { ...activeTab.queryParams, tabId: activeTab.tabId };
        }

        if (
            url !== null &&
            (url.includes("signin-callback") || url.includes("signout-callback") || url.includes("notfound"))
        ) {
            url = "/";
        }

        return [url, queryParams];
    }
}
